import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, MapPin, Gift, TrendingUp, Store, Users, Clock, 
  ShieldCheck, Target, Zap, BarChart3, Heart, CheckCircle,
  ChevronDown, Star, Building2, ShoppingBag, Percent, Timer,
  CreditCard, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserCounter } from '../components/UserCounter';

// Logo URLs
const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/bbb41d65-76e2-415c-a352-b5d59d6a4f2b/images/94c4575701ab4ea10689cd42c56d8473de941da23dbc288b081a30a3e38e422b.png";
const FAVICON_URL = "https://static.prod-images.emergentagent.com/jobs/bbb41d65-76e2-415c-a352-b5d59d6a4f2b/images/424c05e5022207a15251fdaea8f7056f71af0bd2eb4536603c39816bfabb1bd9.png";

export const LandingPage = () => {
  const { t } = useTranslation();
  const [activeAudience, setActiveAudience] = useState('shopper');
  const [openFaq, setOpenFaq] = useState(null);
  const [countdown, setCountdown] = useState({ hours: 14, mins: 32 });
  
  // Check if user is logged in as business
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isBusiness = user?.role === 'business';

  // Simulated countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev.mins > 0) return { ...prev, mins: prev.mins - 1 };
        if (prev.hours > 0) return { hours: prev.hours - 1, mins: 59 };
        return { hours: 23, mins: 59 };
      });
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const getStatsData = () => ({
    shopper: [
      { value: t('stat_shopper_1_value'), label: t('stat_shopper_1_label') },
      { value: t('stat_shopper_2_value'), label: t('stat_shopper_2_label') },
      { value: t('stat_shopper_3_value'), label: t('stat_shopper_3_label') },
    ],
    business: [
      { value: t('stat_business_1_value'), label: t('stat_business_1_label') },
      { value: t('stat_business_2_value'), label: t('stat_business_2_label') },
      { value: t('stat_business_3_value'), label: t('stat_business_3_label') },
    ]
  });

  const getHowItWorksShoppers = () => [
    { step: '01', icon: <MapPin className="w-6 h-6" />, title: t('how_shopper_1_title'), desc: t('how_shopper_1_desc') },
    { step: '02', icon: <ShoppingBag className="w-6 h-6" />, title: t('how_shopper_2_title'), desc: t('how_shopper_2_desc') },
    { step: '03', icon: <Gift className="w-6 h-6" />, title: t('how_shopper_3_title'), desc: t('how_shopper_3_desc') },
  ];

  const getHowItWorksBusiness = () => [
    { step: '01', icon: <Store className="w-6 h-6" />, title: t('how_business_1_title'), desc: t('how_business_1_desc') },
    { step: '02', icon: <Target className="w-6 h-6" />, title: t('how_business_2_title'), desc: t('how_business_2_desc') },
    { step: '03', icon: <Users className="w-6 h-6" />, title: t('how_business_3_title'), desc: t('how_business_3_desc') },
  ];

  const getBenefitsShopper = () => [
    { icon: <Gift />, title: t('benefit_shopper_1_title'), desc: t('benefit_shopper_1_desc') },
    { icon: <Clock />, title: t('benefit_shopper_2_title'), desc: t('benefit_shopper_2_desc') },
    { icon: <ShieldCheck />, title: t('benefit_shopper_3_title'), desc: t('benefit_shopper_3_desc') },
    { icon: <MapPin />, title: t('benefit_shopper_4_title'), desc: t('benefit_shopper_4_desc') },
  ];

  const getBenefitsBusiness = () => [
    { icon: <Target />, title: t('benefit_business_1_title'), desc: t('benefit_business_1_desc') },
    { icon: <Zap />, title: t('benefit_business_2_title'), desc: t('benefit_business_2_desc') },
    { icon: <Heart />, title: t('benefit_business_3_title'), desc: t('benefit_business_3_desc') },
    { icon: <BarChart3 />, title: t('benefit_business_4_title'), desc: t('benefit_business_4_desc') },
  ];

  const getFaqs = () => [
    { q: t('faq_1_q'), a: t('faq_1_a') },
    { q: t('faq_2_q'), a: t('faq_2_a') },
    { q: t('faq_3_q'), a: t('faq_3_a') },
    { q: t('faq_4_q'), a: t('faq_4_a') },
    { q: t('faq_5_q'), a: t('faq_5_a') },
  ];

  // Sample coupon for visual demonstration
  const SampleCoupon = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, rotate: -2 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 w-full max-w-sm"
    >
      {/* Coupon Header */}
      <div className="relative h-32 bg-gradient-to-br from-emerald-500 to-emerald-600 p-4">
        <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-white text-xs font-medium flex items-center gap-1">
          <Timer className="w-3 h-3" />
          {countdown.hours}h {countdown.mins}m
        </div>
        <div className="absolute bottom-4 left-4">
          <span className="text-emerald-100 text-sm">{t('sample_coupon_flash')}</span>
          <h3 className="text-white font-bold text-lg">{t('sample_coupon_business')}</h3>
        </div>
      </div>
      
      {/* Coupon Body */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('sample_coupon_menu')}</p>
            <p className="text-gray-400 line-through text-sm">15,90€</p>
          </div>
          <div className="bg-emerald-100 dark:bg-emerald-900/30 rounded-xl px-4 py-2 text-center">
            <span className="text-emerald-700 dark:text-emerald-400 font-bold text-2xl">-25%</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
          <MapPin className="w-4 h-4" />
          <span>{t('sample_coupon_distance')}</span>
          <span className="mx-1">•</span>
          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
          <span>4.7</span>
        </div>
        
        <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span className="text-sm text-gray-600 dark:text-gray-400">+12 puntos</span>
          </div>
          <span className="text-emerald-600 font-bold">11,92€</span>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Floating Business Action Bar */}
      {isBusiness && (
        <motion.div 
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed top-20 left-1/2 -translate-x-1/2 z-40 bg-local-sage text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-4"
          data-testid="business-action-bar"
        >
          <span className="text-sm font-medium hidden sm:inline">
            ¡Hola, {user?.businessName || 'Negocio'}!
          </span>
          <Link to="/business/kupons?action=create">
            <Button size="sm" className="bg-white text-local-sage hover:bg-white/90 rounded-full gap-2">
              <Zap className="w-4 h-4" />
              Crear Kupon Ahora
            </Button>
          </Link>
          <Link to="/business/dashboard" className="text-white/80 hover:text-white text-sm underline">
            Ir al Dashboard
          </Link>
        </motion.div>
      )}
      
      {/* Hero Section */}
      <section className="relative py-12 lg:py-20 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-emerald-100/50 to-transparent dark:from-emerald-900/20 dark:to-transparent" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          {/* Audience Toggle */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-10"
          >
            <div className="inline-flex bg-white dark:bg-gray-800 rounded-full p-1 sm:p-1.5 shadow-lg border border-gray-200 dark:border-gray-700">
              <Link
                to="/compradores"
                className={`flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                  activeAudience === 'shopper' 
                    ? 'bg-emerald-600 text-white shadow-md' 
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                }`}
                data-testid="toggle-shopper"
              >
                <ShoppingBag className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">{t('toggle_i_am')} </span>{t('toggle_shopper')}
              </Link>
              <Link
                to="/para-negocios"
                className={`flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                  activeAudience === 'business' 
                    ? 'bg-emerald-600 text-white shadow-md' 
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                }`}
                data-testid="toggle-business"
              >
                <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">{t('toggle_i_am')} </span>{t('toggle_business')}
              </Link>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1 text-center lg:text-left">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeAudience}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  {activeAudience === 'shopper' ? (
                    <>
                      <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                        {t('shopper_badge')}
                      </span>
                      <h1 className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-4 sm:mb-6 leading-tight text-gray-900 dark:text-white">
                        {t('shopper_headline_1')}
                        <span className="block text-emerald-600 mt-1 sm:mt-2">{t('shopper_headline_2')}</span>
                      </h1>
                      <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0" dangerouslySetInnerHTML={{ __html: t('shopper_description') }} />
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                        <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                        {t('business_badge')}
                      </span>
                      <h1 className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-4 sm:mb-6 leading-tight text-gray-900 dark:text-white">
                        {t('business_headline_1')}
                        <span className="block text-emerald-600 mt-1 sm:mt-2">{t('business_headline_2')}</span>
                      </h1>
                      <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0" dangerouslySetInnerHTML={{ __html: t('business_description') }} />
                    </>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* CTAs */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-10 justify-center lg:justify-start"
              >
                <Link to="/register" data-testid="hero-cta-primary">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full sm:w-auto shadow-lg shadow-emerald-600/25">
                    {activeAudience === 'shopper' ? t('cta_shopper_primary') : t('cta_business_primary')}
                    <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </Link>
                <Link to="#como-funciona" data-testid="hero-cta-secondary">
                  <Button variant="outline" className="rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full sm:w-auto border-2">
                    {t('cta_shopper_secondary')}
                  </Button>
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="grid grid-cols-3 gap-3 sm:gap-6"
              >
                {getStatsData()[activeAudience].map((stat, i) => (
                  <div key={i} className="text-center lg:text-left">
                    <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-emerald-600">{stat.value}</div>
                    <div className="text-[10px] sm:text-xs lg:text-sm text-gray-500 dark:text-gray-400 mt-1 leading-tight">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Right Visual - Sample Coupon (hidden on mobile, shown on lg+) */}
            <div className="order-1 lg:order-2 hidden lg:flex justify-center lg:justify-end">
              <div className="relative">
                {/* Decorative elements */}
                <div className="absolute -top-8 -right-8 w-24 h-24 bg-emerald-200 dark:bg-emerald-800 rounded-full blur-3xl opacity-60" />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-amber-200 dark:bg-amber-800 rounded-full blur-3xl opacity-40" />
                
                <SampleCoupon />
                
                {/* Floating badges */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="absolute -left-4 top-1/4 bg-white dark:bg-gray-800 rounded-xl p-3 shadow-lg border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('sample_coupon_points')}</p>
                      <p className="font-bold text-gray-900 dark:text-white">2,450</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 }}
                  className="absolute -right-4 bottom-1/4 bg-white dark:bg-gray-800 rounded-xl p-3 shadow-lg border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('sample_coupon_saved')}</p>
                      <p className="font-bold text-emerald-600">127€</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Points Redemption Banner - 50,000 Users Milestone */}
      <section className="py-6 px-4">
        <div className="container mx-auto">
          <UserCounter variant="banner" className="max-w-4xl mx-auto" />
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-4 sm:py-6 bg-gray-50 dark:bg-gray-800/50 border-y border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-6 lg:gap-12 text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              <span className="text-xs sm:text-sm font-medium">Comercios verificados</span>
            </div>
            <div className="flex items-center gap-2">
              <Timer className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              <span className="text-xs sm:text-sm font-medium">Ofertas flash 24-72h</span>
            </div>
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              <span className="text-xs sm:text-sm font-medium">Puntos sin caducidad</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 fill-emerald-600" />
              <span className="text-xs sm:text-sm font-medium">Recompensas reales</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section (Business only) */}
      {activeAudience === 'business' && (
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-manrope font-bold text-3xl sm:text-4xl mb-4 text-gray-900 dark:text-white">
                El marketing local está roto
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {[
                { icon: '😤', title: 'Redes sin alcance', desc: 'Publicas, nadie lo ve. El algoritmo muestra tus posts a un 2%.' },
                { icon: '💸', title: 'Agencias caras', desc: 'Meses de contrato, informes vacíos, y menos clientes al final.' },
                { icon: '📮', title: 'Flyers que nadie lee', desc: '500 flyers, 3 llamadas. Coste por cliente: impensable.' },
                { icon: '✅', title: 'LokAlGrow funciona', desc: 'Usuarios buscando ofertas. Apareces cuando quieren comprar.', highlight: true },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`rounded-2xl p-6 ${item.highlight 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}`}
                >
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className={`font-bold mb-2 ${item.highlight ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{item.title}</h3>
                  <p className={`text-sm ${item.highlight ? 'text-emerald-100' : 'text-gray-600 dark:text-gray-400'}`}>{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section id="como-funciona" className="py-12 sm:py-16 lg:py-24 bg-gray-50 dark:bg-gray-800/30">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="font-manrope font-bold text-2xl sm:text-3xl lg:text-4xl xl:text-5xl mb-3 sm:mb-4 text-gray-900 dark:text-white">
              {activeAudience === 'shopper' ? t('how_section_title_shopper') : t('how_section_title_business')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">
              {activeAudience === 'shopper' 
                ? t('how_section_subtitle_shopper')
                : t('how_section_subtitle_business')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-5xl mx-auto">
            {(activeAudience === 'shopper' ? getHowItWorksShoppers() : getHowItWorksBusiness()).map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative"
              >
                {/* Connector line */}
                {i < 2 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-emerald-300 to-emerald-100 dark:from-emerald-700 dark:to-emerald-900" />
                )}
                
                <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-8 shadow-lg border border-gray-100 dark:border-gray-700 relative z-10">
                  <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl sm:rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600">
                      {item.icon}
                    </div>
                    <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-emerald-200 dark:text-emerald-800">{item.step}</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-gray-900 dark:text-white">{item.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-manrope font-bold text-3xl sm:text-4xl lg:text-5xl mb-4 text-gray-900 dark:text-white">
              {activeAudience === 'shopper' ? t('benefits_section_title_shopper') : t('benefits_section_title_business')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {(activeAudience === 'shopper' ? getBenefitsShopper() : getBenefitsBusiness()).map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-md border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:border-emerald-200 dark:hover:border-emerald-800 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 mb-4 group-hover:scale-110 transition-transform">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">{benefit.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator for Business */}
      {activeAudience === 'business' && (
        <section className="py-16 lg:py-24 bg-gray-50 dark:bg-gray-800/30">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <div className="text-center mb-10">
                <h2 className="font-manrope font-bold text-3xl sm:text-4xl mb-4 text-gray-900 dark:text-white">
                  ¿Cuánto vale un cliente que vuelve?
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  Un cliente nuevo no vale solo lo que gasta hoy. Vale todo lo que gastará mientras siga siendo tu cliente.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  {[
                    { value: '−15€', label: 'Descuento dado', color: 'red' },
                    { value: '50€', label: 'Ticket medio', color: 'gray' },
                    { value: '×10', label: 'Visitas/año', color: 'gray' },
                    { value: '500€', label: 'Valor anual', color: 'emerald' },
                    { value: '×33', label: 'ROI cupón', color: 'amber' },
                  ].map((item, i) => (
                    <div key={i} className={`p-4 rounded-xl text-center ${
                      item.color === 'red' ? 'bg-red-50 dark:bg-red-900/20' :
                      item.color === 'emerald' ? 'bg-emerald-50 dark:bg-emerald-900/20' :
                      item.color === 'amber' ? 'bg-amber-50 dark:bg-amber-900/20 col-span-2 sm:col-span-1' :
                      'bg-gray-50 dark:bg-gray-700/50'
                    }`}>
                      <div className={`text-2xl font-bold ${
                        item.color === 'red' ? 'text-red-600' :
                        item.color === 'emerald' ? 'text-emerald-600' :
                        item.color === 'amber' ? 'text-amber-600' :
                        'text-gray-900 dark:text-white'
                      }`}>{item.value}</div>
                      <div className="text-gray-600 dark:text-gray-400 text-xs mt-1">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Annual Spending Stats for Shoppers */}
      {activeAudience === 'shopper' && (
        <section className="py-16 lg:py-24 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800/50">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-sm font-semibold mb-4">
                <TrendingUp className="w-4 h-4" />
                {t('spending_badge') || 'El dinero que ya gastas'}
              </span>
              <h2 className="font-manrope font-bold text-3xl sm:text-4xl mb-4 text-gray-900 dark:text-white">
                {t('spending_title') || 'Cada euro local puede trabajar para ti'}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {t('spending_description') || 'El consumidor promedio gasta entre 3.000€ y 6.000€ al año en comercios locales. Con LokAlGrow, ese gasto se convierte en recompensas reales.'}
              </p>
            </motion.div>

            {/* Potential Rewards Highlight */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-2xl p-8 text-center mb-12 max-w-3xl mx-auto shadow-xl"
            >
              <p className="text-emerald-100 text-lg mb-2">{t('spending_potential_label') || 'Recompensas potenciales al año'}</p>
              <p className="text-5xl sm:text-6xl font-bold text-white mb-2">+500€</p>
              <p className="text-emerald-100">{t('spending_potential_desc') || 'con el mismo gasto que ya haces. Sin gastar un euro más.'}</p>
            </motion.div>

            {/* Spending Breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-5xl mx-auto">
              {[
                { icon: '🛒', amount: '~1.500€', period: '/año', label: t('spending_food') || 'Alimentación local', sublabel: '25-30€/semana' },
                { icon: '🍕', amount: '~900€', period: '/año', label: t('spending_restaurants') || 'Restaurantes y cafés', sublabel: 'Comidas fuera' },
                { icon: '👗', amount: '~480€', period: '/año', label: t('spending_fashion') || 'Moda y ropa', sublabel: 'Comercios del barrio' },
                { icon: '💈', amount: '~300€', period: '/año', label: t('spending_services') || 'Servicios personales', sublabel: 'Peluquería, estética' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg border border-gray-100 dark:border-gray-700 text-center hover:shadow-xl transition-shadow"
                >
                  <div className="text-4xl mb-3">{item.icon}</div>
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-600 mb-1">
                    {item.amount}<span className="text-sm text-gray-400">{item.period}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{item.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.sublabel}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Rewards Preview for Shoppers */}
      {activeAudience === 'shopper' && (
        <section className="py-16 lg:py-24 bg-gray-50 dark:bg-gray-800/30">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="font-manrope font-bold text-3xl sm:text-4xl mb-4 text-gray-900 dark:text-white">
                {t('rewards_title') || 'Tus recompensas te esperan'}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { icon: '🏨', title: 'Noches de Hotel', desc: 'Estancias en hoteles seleccionados', tag: 'Alta demanda', tagColor: 'bg-red-100 text-red-700' },
                { icon: '🍽', title: 'Cenas en Restaurantes', desc: 'Desde cenas románticas hasta comidas familiares', tag: 'Disponible', tagColor: 'bg-emerald-100 text-emerald-700' },
                { icon: '✨', title: 'Experiencias Únicas', desc: 'Actividades, escapadas, talleres', tag: 'Nuevo', tagColor: 'bg-purple-100 text-purple-700' },
              ].map((reward, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700 text-center hover:shadow-xl transition-shadow"
                >
                  <div className="text-5xl mb-4">{reward.icon}</div>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 ${reward.tagColor}`}>
                    {reward.tag}
                  </span>
                  <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">{reward.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{reward.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-manrope font-bold text-3xl sm:text-4xl mb-4 text-gray-900 dark:text-white">
              Respuestas directas
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-3">
            {getFaqs().map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  data-testid={`faq-${i}`}
                >
                  <span className="font-semibold text-gray-900 dark:text-white pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5 text-gray-600 dark:text-gray-400">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-br from-emerald-600 to-emerald-700 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 left-0 w-32 sm:w-64 h-32 sm:h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-48 sm:w-96 h-48 sm:h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-manrope font-bold text-2xl sm:text-3xl lg:text-4xl xl:text-5xl mb-4 sm:mb-6 text-white px-2">
              {activeAudience === 'shopper' 
                ? 'El gasto ya está hecho. ¿Y las recompensas?'
                : 'Tu próximo cliente está en LokAlGrow'}
            </h2>
            <p className="text-emerald-100 text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 px-4">
              {activeAudience === 'shopper'
                ? 'Únete gratis hoy. Empieza a acumular desde tu próxima compra.'
                : 'Publica tu primera oferta hoy. Sin inversión inicial.'}
            </p>
            <Link to="/register" data-testid="final-cta">
              <Button className="bg-white text-emerald-600 hover:bg-gray-100 rounded-full px-6 sm:px-8 lg:px-10 py-5 sm:py-6 lg:py-7 text-sm sm:text-base lg:text-lg font-semibold shadow-xl">
                {activeAudience === 'shopper' ? 'Unirme a LokAlGrow — Gratis' : 'Unirme como negocio'}
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </Link>
            <p className="text-emerald-200 text-xs sm:text-sm mt-4 sm:mt-6">
              {activeAudience === 'shopper' 
                ? 'Sin tarjeta de crédito. Sin compromiso. Siempre gratis.'
                : 'Primeros pasos en menos de 10 minutos'}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
